import { ReactNode, RefObject, useEffect } from "react"
import { gsap } from "gsap"
import { Text } from "../../../components"
import { resolveIcon } from "../../../helpers"
import { API_BASE_URL } from '../../../config'

export const trackLinkClick = (linkId: number) => {
    fetch(`${API_BASE_URL}/metric?link_id=${linkId}`, {method: 'POST', keepalive: true}).catch(() => {})
}

const trackStoreOpen = (storeId: number, type: 'map' | 'card') => {
    fetch(`${API_BASE_URL}/metric`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({store_id: storeId, type}),
        keepalive: true,
    }).catch(() => {})
}

export const trackMapClick = (storeId: number) => trackStoreOpen(storeId, 'map')

export const trackCardClick = (storeId: number) => trackStoreOpen(storeId, 'card')

export interface ApiLink {
    id: number;
    store_id: number;
    link: string;
    icon: string | null;
    label: string;
}

export type PageStyle = 'default' | 'cover' | 'blur' | 'card'

export const PAGE_STYLES: {key: PageStyle; label: string}[] = [
    {key: 'default', label: 'Классический'},
    {key: 'cover', label: 'Обложка'},
    {key: 'blur', label: 'Размытие'},
    {key: 'card', label: 'Карточка'},
]

// Бэкенд может вернуть неизвестное значение — тогда показываем стиль по умолчанию
export const normalizePageStyle = (style: string | null | undefined): PageStyle =>
    PAGE_STYLES.some((s) => s.key === style) ? style as PageStyle : 'default'

export interface StyleData {
    style: string,
    acent_color: string | null,
}

export interface StoreData {
    id: number;
    title: string;
    subtitle: string;
    image: string;
    style?: StyleData | null;
    raiting?: string | null;
    isPartner?: boolean;
    spending_points?: number;
    obtaining_points?: number;
    links: ApiLink[];
}

export interface StoreStyleProps {
    data: StoreData;
    // Вместо публичного списка ссылок — например, редактируемые строки в админке
    children?: ReactNode;
    // Блок между описанием и ссылками — например, оценка заведения
    rating?: ReactNode;
    className?: string;
    animateLinks?: boolean;
    // Переход по публичной ссылке — например, для начисления баллов
    onLinkOpen?: (linkId: number) => void;
    // Ссылки, за которые баллы уже получены
    openedLinkIds?: number[];
    pointsPerLink?: number;
}

interface LinkRowProps extends ApiLink {
    onOpen?: (linkId: number) => void;
    opened?: boolean;
    points?: number;
}

export const LinkRow = ({id, icon, label, link, onOpen, opened, points}: LinkRowProps) => {
    const iconSrc = resolveIcon(icon)
    const handleClick = () => {
        trackLinkClick(id)
        onOpen?.(id)
        window.open(link, "_blank", "noopener,noreferrer")
    }
    return (
        <div className={`link-row${opened ? ' link-row-opened' : ''}`} onClick={handleClick}>
            {iconSrc && <img className="link-icon" src={iconSrc} alt="" />}
            <Text size="m" color="white">{label}</Text>
            {opened && (
                <span className="link-points" title="Баллы за переход получены">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M2.5 6.5l2.5 2.5 4.5-5" />
                    </svg>
                    +{points}
                </span>
            )}
            <span className="link-arrow">→</span>
        </div>
    )
}

export const useLinksAppear = (rootRef: RefObject<HTMLElement | null>, enabled = true) => {
    useEffect(() => {
        if (!enabled) return
        const ctx = gsap.context(() => {
            gsap.set('.link-row', {transition: 'none'})
            gsap.from('.link-row', {
                opacity: 0,
                y: 16,
                duration: 0.5,
                ease: 'power3.out',
                stagger: 0.08,
                delay: 0.15,
                clearProps: 'all',
            })
        }, rootRef)
        return () => ctx.revert()
    }, [rootRef, enabled])
}
